import { Alert, Button, Divider, Typography } from '@customink/pigment-react';
import {
  useDesktopBreakpoint,
  useOrderSetPricingDetails,
  useOrderSetTotal,
  usePaymentToken,
  useProject,
} from '@customink/project-service-react-components';
import { SHIPPING_TYPES } from '@customink/project-service-types';
import { useEffect, useRef, useState } from 'react';

import { useApplePaySession } from './useApplePaySession';

import {
  applePayAnalyticsEvent,
  googleAnalyticsTag,
} from '@/components/vendors/googleAnalytics';
import { useStickyBarContext } from '@/contexts/StickyBarContext';
import { useCheckoutEnabled } from '@/hooks/Project';
import { isApplePaySupported } from '@/utils/applePayUtils';
import { APPLE_PAY_FLAG_NAME } from '@/utils/featureFlags';
import { signaler } from '@/utils/signaler';

type ApplePayButtonProps = {
  className?: string;
};

export default function ApplePayButton({ className }: ApplePayButtonProps) {
  // Keep track of whether we've seen a project before
  const hasSeenProject = useRef(false);
  const analyticsTagSent = useRef(false);

  const isDesktop = useDesktopBreakpoint();
  const { setIsApplePayVisible } = useStickyBarContext();

  const { data: project, isLoading: isProjectLoading } = useProject();
  const isIndividualShipping =
    project?.configuration?.shippingType === SHIPPING_TYPES.INDIVIDUAL;
  const isCheckoutEnabled = useCheckoutEnabled();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasActiveCards, setHasActiveCards] = useState<boolean>(true);

  // Early check for Apple Pay support before doing any API calls
  const isApplePaySupportedOnDevice = isApplePaySupported();

  // Only check the feature flag if conditions are met (mobile + Apple Pay supported)
  const shouldCheckFeatureFlag =
    !isDesktop && isApplePaySupportedOnDevice && !isIndividualShipping;
  const isFeatureFlagEnabled = shouldCheckFeatureFlag
    ? signaler.featureFlag(APPLE_PAY_FLAG_NAME) === 'test'
    : false;

  // Effect to send analytics tag once when feature flag check is relevant
  useEffect(() => {
    if (shouldCheckFeatureFlag && !analyticsTagSent.current) {
      const flagValue = signaler.featureFlag(APPLE_PAY_FLAG_NAME);
      googleAnalyticsTag({
        event: 'ab_test_group',
        ab_test_name: '2025 04 30 Apple Pay Express',
        ab_test_location: 'project_cart',
        ab_test_group: flagValue,
      });
      analyticsTagSent.current = true;
    }
  }, [shouldCheckFeatureFlag]);

  // Skip data fetching when we know for sure we won't need Apple Pay
  const shouldSkipDataFetching =
    isDesktop ||
    !isApplePaySupportedOnDevice ||
    !isFeatureFlagEnabled ||
    isIndividualShipping;

  const { data: paymentTokenData, isLoading: isPaymentTokenLoading } =
    usePaymentToken({
      skip: shouldSkipDataFetching,
    });
  const paymentToken = paymentTokenData?.token ?? '';

  const {
    data: totalData,
    isLoading: isTotalLoading,
    refetch: refetchTotal,
  } = useOrderSetTotal({
    skip: shouldSkipDataFetching,
  });

  const {
    data: pricingDetailsData,
    isLoading: isPricingDetailsLoading,
    refetch: refetchPricing,
  } = useOrderSetPricingDetails({
    skip: shouldSkipDataFetching,
  });

  // Force refetch when the project changes
  useEffect(() => {
    if (shouldSkipDataFetching || !project) {
      return;
    }

    if (hasSeenProject.current) {
      refetchTotal();
      refetchPricing();
    } else {
      hasSeenProject.current = true;
    }
  }, [project, shouldSkipDataFetching, refetchTotal, refetchPricing]);

  const {
    beginSession,
    isApplePayAvailable,
    isSessionInProgress,
    hasActiveApplePayCards,
  } = useApplePaySession({
    paymentToken,
    onError: (error) => setErrorMessage(error),
    totalData,
    pricingDetailsData,
  });

  useEffect(() => {
    if (paymentToken) {
      setErrorMessage(null);
    }
  }, [paymentToken]);

  // Update hasActiveCards when hasActiveApplePayCards changes
  useEffect(() => {
    setHasActiveCards(hasActiveApplePayCards);
  }, [hasActiveApplePayCards]);

  // Check if all required data is loaded
  const isDataLoading =
    isPaymentTokenLoading ||
    isTotalLoading ||
    isPricingDetailsLoading ||
    isProjectLoading;

  const shouldRenderButton =
    !shouldSkipDataFetching && !isDataLoading && isApplePayAvailable;

  useEffect(() => {
    setIsApplePayVisible(shouldRenderButton);
    return () => {
      setIsApplePayVisible(false);
    };
  }, [shouldRenderButton, setIsApplePayVisible]);

  if (!shouldRenderButton) {
    return null;
  }

  const handleApplePayButtonClick = () => {
    applePayAnalyticsEvent('payment button click');
    beginSession();
  };

  return (
    <>
      {isDesktop && (
        <Divider sx={{ margin: '8px 0' }}>
          <Typography variant="uiTextSmall">or</Typography>
        </Divider>
      )}
      <Button
        className={className}
        data-testid="apple-pay-button"
        data-type={hasActiveCards ? 'check-out' : 'set-up'}
        disabled={isSessionInProgress || !isCheckoutEnabled}
        onClick={handleApplePayButtonClick}
        size="medium"
        sx={{
          WebkitAppearance: '-apple-pay-button',
          '-apple-pay-button-type': hasActiveCards ? 'check-out' : 'set-up',
          '-apple-pay-button-style': 'black',
          width: '100%',
          color: 'white',
          backgroundColor: 'black',
          marginTop: !isDesktop ? '8px' : '0',
        }}
      />
      {errorMessage && (
        <Alert severity="error" sx={{ mt: '0.25rem' }}>
          {errorMessage}
        </Alert>
      )}
    </>
  );
}
